import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBlogPosts } from '../../hooks/useBlogPosts';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';

const BlogCategory = () => {
  const { category } = useParams();
  const { fetchPosts, posts, loading, error } = useBlogPosts();

  useEffect(() => {
    if (category) {
      fetchPosts({
        published: true,
        status: 'published',
        category: decodeURIComponent(category)
      });
    }
  }, [category, fetchPosts]);

  const categoryName = decodeURIComponent(category);

  // Loading state
  if (loading && posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-10 w-32 bg-muted rounded mb-4"></div>
          <div className="h-12 w-64 bg-muted rounded mb-8"></div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-64 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <div className="mb-6">
        <Link
          to="/blog"
          className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Link>
      </div>

      {/* Header */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-4">
          <Badge variant="secondary" className="text-lg px-4 py-2">
            {categoryName}
          </Badge>
        </div>
        <h1 className="text-4xl font-bold mb-4">
          {categoryName} Articles
        </h1>
        <p className="text-xl text-muted-foreground">
          {posts.length} {posts.length === 1 ? 'article' : 'articles'} in this category
        </p>
      </div>

      {/* Error State */}
      {error && (
        <Card className="mb-8">
          <CardContent className="p-6 text-center">
            <div className="text-destructive mb-2">Error loading blog posts</div>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Blog Posts */}
      {posts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-muted-foreground mb-2">
              No published posts in this category yet
            </div>
            <Link
              to="/blog"
              className="text-primary hover:underline text-sm"
            >
              Browse all articles
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <article key={post.id} className="group">
              <Card className="h-full hover:shadow-lg transition-shadow">
                {/* Featured Image */}
                {post.featuredImage && (
                  <div className="aspect-video overflow-hidden rounded-t-lg">
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}

                <CardHeader className="pb-4">
                  {/* Categories (excluding current category) */}
                  {post.categories && post.categories.length > 1 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {post.categories
                        .filter(cat => cat !== categoryName)
                        .slice(0, 2)
                        .map((cat, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {cat}
                          </Badge>
                        ))}
                    </div>
                  )}

                  {/* Title */}
                  <CardTitle className="line-clamp-2 group-hover:text-primary transition-colors">
                    <Link to={`/blog/${post.slug}`}>
                      {post.title}
                    </Link>
                  </CardTitle>

                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      <span>{post.author?.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>
                        {new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Excerpt */}
                  {post.excerpt && (
                    <p className="text-muted-foreground line-clamp-3 mb-4">
                      {post.excerpt}
                    </p>
                  )}

                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {post.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-muted text-muted-foreground"
                        >
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Read More Link */}
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center text-primary hover:underline text-sm font-medium"
                  >
                    Read more →
                  </Link>
                </CardContent>
              </Card>
            </article>
          ))}
        </div>
      )}

      {/* Load More */}
      {posts.length > 0 && (
        <div className="text-center mt-12">
          <button
            onClick={() => fetchPosts({ 
              published: true, 
              status: 'published',
              category: categoryName,
              startAfter: posts[posts.length - 1]
            })}
            disabled={loading}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load More Posts'}
          </button>
        </div>
      )}
    </div>
  );
};

export default BlogCategory;
